import { Component, OnInit, ViewEncapsulation, Inject, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { combineLatest, Observable, of, Subscription, zip } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { AppFacade } from '@app/app.facade';
import { Airport } from '@app/modules/core/interfaces/airport';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { SearchType } from '@search-bar/entities/search-type';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { PlacePipe } from '@app/pipes/place.pipe';
import { BRANCH_CODE } from '@app/modules/core/constants/query-params';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { PassengerArrangement, PassengerType, SeatMaxQuantity } from '@shared/base-search-bar/entities/passenger-arrangement';
import { mapPayload } from '@buses/bus-results/mappers/search-payload.mapper';
import { BusSearchPayload } from '@buses/bus-results/entities/bus-search-payload';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { BusType } from '@search-bar/entities/bus-type';
import { BusClassTypes } from '@buses/bus-results/entities/bus-type';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { dateValidationByBuses } from '@buses/bus-results/mappers/date-validation.mapper';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { FlightType } from '@search-bar/entities/flight-type';

@Component({
  selector: 'app-bus-home',
  templateUrl: './bus-home.container.html',
  styleUrls: ['./bus-home.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BusHomeContainer implements OnInit, OnDestroy {

  public searchType: SearchType = SearchType.HOME_BUSES;
  public source: EVENT_SOURCE | string = EVENT_SOURCE.TRAVEL_HOME;
  public loadDefaultplaces: boolean;
  public params: BusSearchPayload = {} as BusSearchPayload;
  public sortBuses: string = SorterTypes.Recommended;
  public busType: BusType;
  public busClass: BusClassTypes;
  public sortbus: string = SorterTypes.Recommended;
  public validDates: boolean;

  private calendarRoute: string = 'calendar';
  private readonly subscriptions: Subscription[];

  constructor(
    private readonly facade: AppFacade,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly baseSearchBarFacade: BaseSearchBarFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly placePipe: PlacePipe,
    @Inject(IS_BROWSER) public isBrowser: boolean,
  ) {
    this.subscriptions = [];
    this.validDates = true;
  }


  ngOnInit(): void {
    if (this.isBrowser) {
      this.loadDefaultplaces = true;
      this.baseSearchBarFacade.resetBranchCode();
      this.baseSearchBarFacade.resetSalesChannel();
      this.baseSearchBarFacade.resetPackageStep();
      this.subscriptions.push(this.searchBuses());
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get airports$(): Observable<Airport[]> {
    return this.facade.airports$;
  }

  private searchBuses(): Subscription {
    return combineLatest(
      [this.route?.paramMap,
        this.route?.queryParamMap?.pipe(take(1))]
    ).pipe(
      switchMap(([params, queries]) => zip(
        of({ params, queries }),
        this.placePipe.transform(params.get('arrival')), this.placePipe.transform(params.get('departure')),
        this.baseSearchBarFacade.arrivalTerminal$, this.baseSearchBarFacade.departureTerminal$
      )),
      map(data => ({
        ...data[0], arrival: data[1], departure: data[2],
        arrivalStateCode: data[3].statecode,
        departureStatecode: data[4].statecode
      })),
      tap(({
        params, queries, arrival, departure,
        arrivalStateCode, departureStatecode }) => {
        if (this.validatePopulated(params)) {
          this.loadDefaultplaces = false;
          const branchCode = queries.get(BRANCH_CODE);
          const salesChannel = queries.get('sales_channel');
          const departureTerminal = {
            code: departure.cityCode, country: departure.country,
            description: departure.name ? departure.name.split(',')[0] : '',
            statecode: departureStatecode
          } as SelectAutocompletePlace;
          const arrivalTerminal = {
            code: arrival?.cityCode, country: arrival.country,
            description: arrival.name ? arrival.name.split(',')[0] : '',
            statecode: arrivalStateCode
          } as SelectAutocompletePlace;
          this.searchBusByParams(params);
          this.setPassengerArrangement({
            id: PassengerType.ADULTS,
            quantity: this.params.Adults && parseInt(this.params.Adults) <= SeatMaxQuantity.ADULTS ?
              parseInt(this.params.Adults) : 1
          });
          this.baseSearchBarFacade.selectPlace(
            { ...arrivalTerminal, type: PlaceType.ARRIVAL }, false, this.searchType);
          this.baseSearchBarFacade.selectPlace(
            { ...departureTerminal, type: PlaceType.DEPARTURE }, false, this.searchType);
          if (branchCode) {
            this.baseSearchBarFacade.setBranchCode(branchCode);
          }
          if (salesChannel) {
            this.baseSearchBarFacade.setSalesChannel(salesChannel);
          }
        }
      }),
      switchMap(({ params, queries,
        arrival, departure }) => zip(
        of({
          params, queries,
          arrival, departure
        }),
        this.baseSearchBarFacade.searchData$
      )),
      map(data => ({
        ...data[0],
        searchData: data[1]
      })),
      tap(({
        searchData,
        params,
        queries }) => {
        this.params.RequestSettings.BranchCode = searchData.branchCode;
        this.params.RequestSettings.SalesChanelCode = searchData.salesChannel;
        this.payloadBusSearchData(searchData, params, queries);
      })
    ).subscribe();
  }

  private searchBusByParams(params: ParamMap): void {
    this.params = mapPayload(params['params'], true);
    this.busType = params.get('bus_type').toUpperCase() as BusType;
    this.busClass = params.get('bus_type').toUpperCase() as BusClassTypes;
    this.sortBuses = SorterTypes.Recommended;
  }

  private setPassengerArrangement(event: PassengerArrangement): void {
    this.baseSearchBarFacade.setPassengerArrangement(event);
  }

  private payloadBusSearchData(searchData: SearchData, params: ParamMap, queries: ParamMap): void {
    const payloadBusSearch = { ...searchData };
    let dateValidation;
    let departureDateParams = null;
    let arrivalDateParams = null;
    if (this.busType === BusType.ONEWAY) {
      departureDateParams = formatDateByRegex(params.get('departure_date'));
      dateValidation = dateValidationByBuses(departureDateParams, this.busType);
      payloadBusSearch.departureDate = dateValidation.departure;
    } else if (this.busType === BusType.ROUNDTRIP) {
      departureDateParams = formatDateByRegex(params.get('departure_date'));
      arrivalDateParams = formatDateByRegex(params.get('arrival_date'));
      dateValidation = dateValidationByBuses(departureDateParams, this.busType, arrivalDateParams);
      payloadBusSearch.departureDate = dateValidation.departure;
      payloadBusSearch.arrivalDate = dateValidation.arrival;
    }
    payloadBusSearch.flightType = this.busType;
    payloadBusSearch.airline = params.get('company');
    this.validDates = !!dateValidation && dateValidation.valid;
    if (this.validDates) {
      this.baseSearchBarFacade.selectCalendarDates([departureDateParams, arrivalDateParams]);
      this.baseSearchBarFacade.saveSearchData(payloadBusSearch, SearchBy.BUSES);
      this.baseSearchBarFacade.selectFlightType(this.busType.toString() as FlightType);
    }
  }

  public showMobileCalendar(type: CalendarField): void {
    this.router.navigate(
      [{ outlets: { search: [this.calendarRoute] } }],
      {
        state: {
          activeType: type,
          departurePlaceholder: 'BUS_RESULTS_MODULE.LOCATION_SEARCH.BUS_DEPARTURE',
          returnPlaceholder: 'BUS_RESULTS_MODULE.LOCATION_SEARCH.BUS_RETURN',
          searchType: SearchType.HOME_BUSES
        },
        relativeTo: this.route, skipLocationChange: true
      });
  }

  private validatePopulated(params: ParamMap): boolean {
    return (!!params.get('bus_type') &&
      !!params.get('departure') &&
      !!params.get('arrival') &&
      !!params.get('departure_date') &&
      !!params.get('adults') &&
      !!params.get('company'));
  }
}
